/* ---------- 10. marquee (infinite scroller) ----------
 * [data-marquee] root › track = [data-marquee-track] (fallbacks: .marquee_track, .w-dyn-items) › items.
 * Items are cloned until the track overflows the viewport (.marquee_wrap or the root); the CSS animation
 * reads --marquee-distance / --marquee-duration off the track. data-marquee-speed = px per second (default 70).
 * Reduced motion → no clones, just .is-ready. Re-runnable: window.WebflowMarquee.init() / .destroy().
 */

import { $, $$, clone, setAttrs } from '../lib/dom';

export const MARQUEE = {
  root: 'data-marquee',
  track: 'data-marquee-track',
  speed: 'data-marquee-speed',
  clone: 'data-marquee-clone',
  ready: 'is-ready',
  defaultSpeed: 70,
  resizeDelay: 150,
  maxSets: 50,
  reduced: '(prefers-reduced-motion: reduce)',
};
const FOCUSABLE = "a, button, input, select, textarea, summary, [tabindex], [contenteditable='true']";
const store: MarqueeStore = (window.__webflowMarqueeStore ??= { map: new WeakMap(), instances: new Set() }); // survives re-injection

const widthOf = (node: Element) => node.getBoundingClientRect().width;
const marqueeTrack = (root: HTMLElement) =>
  $(`[${MARQUEE.track}]`, root) ??
  $('.marquee_track', root) ??
  $('.marquee_wrap .w-dyn-items', root) ??
  $('.w-dyn-list .w-dyn-items', root) ??
  $('.w-dyn-items', root);
const marqueeSpeed = (root: HTMLElement) => {
  const v = Number(root.getAttribute(MARQUEE.speed));
  return Number.isFinite(v) && v > 0 ? v : MARQUEE.defaultSpeed;
};

/** Deep clone that screen readers, keyboards and pointers all ignore. */
const makeClone = (item: HTMLElement, index: number) => {
  const copy = clone(item);
  setAttrs(copy, { [MARQUEE.clone]: '', 'data-marquee-clone-index': index, 'aria-hidden': 'true' });
  [copy, ...$$('[id]', copy)].forEach((n) => n.removeAttribute('id'));
  if ('inert' in copy) copy.inert = true;
  copy.style.pointerEvents = 'none';
  $$(FOCUSABLE, copy).forEach((n) => setAttrs(n, { tabindex: '-1', 'aria-hidden': 'true' }));
  return copy;
};

/** Resolves once every image in `track` has loaded (or failed) and web fonts are ready — measurements are stable then. */
const settled = (track: HTMLElement) =>
  Promise.all([
    ...$$<HTMLImageElement>('img', track).map((img) =>
      img.complete
        ? null
        : typeof img.decode === 'function'
          ? img.decode().catch(() => {})
          : new Promise((done) => {
              for (const ev of ['load', 'error']) img.addEventListener(ev, done, { once: true });
            }),
    ),
    document.fonts?.ready.catch(() => {}),
  ]);

const createMarquee = (root: HTMLElement): MarqueeInstance | null => {
  const track = marqueeTrack(root);
  if (!track) return null;
  const viewport = $('.marquee_wrap', root) ?? root;
  const motion = window.matchMedia?.(MARQUEE.reduced);
  let observer: ResizeObserver | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let raf = 0;
  let lastWidth = 0;
  let destroyed = false;
  let ready = false;

  const originals = () => [...track.children].filter((n): n is HTMLElement => !n.hasAttribute(MARQUEE.clone));
  const addClones = (items: HTMLElement[]) => track.append(...items.map(makeClone));
  const mark = (on: boolean) => [track, root].forEach((n) => n.classList.toggle(MARQUEE.ready, on));
  const reset = () => {
    mark(false);
    for (const p of ['--marquee-distance', '--marquee-duration']) track.style.removeProperty(p);
    $$(`:scope > [${MARQUEE.clone}]`, track).forEach((n) => n.remove());
  };

  const setup = () => {
    if (destroyed) return;
    reset();
    const items = originals();
    const [first] = items;
    if (!first) return;
    const reduced = !!motion?.matches;
    root.toggleAttribute('data-marquee-reduced', reduced);
    if (reduced) return mark(true);
    const viewportWidth = widthOf(viewport);
    if (!viewportWidth) return;
    addClones(items);
    const firstClone = track.children[items.length];
    const distance = firstClone ? firstClone.getBoundingClientRect().left - first.getBoundingClientRect().left : 0;
    if (!(distance > 0)) return reset();
    for (let sets = 0; track.scrollWidth - distance < viewportWidth && sets < MARQUEE.maxSets; sets++) addClones(items);
    track.style.setProperty('--marquee-distance', `${distance}px`);
    track.style.setProperty('--marquee-duration', `${distance / marqueeSpeed(root)}s`);
    lastWidth = viewportWidth;
    mark(true);
  };

  const schedule = () => {
    if (destroyed) return;
    clearTimeout(timer);
    timer = setTimeout(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(setup);
    }, MARQUEE.resizeDelay);
  };
  const onResize = () => {
    if (!ready) return;
    const w = widthOf(viewport);
    if (Math.abs(w - lastWidth) < 1) return;
    lastWidth = w;
    schedule();
  };

  const init = () => {
    mark(false);
    settled(track).then(() => {
      if (destroyed) return;
      requestAnimationFrame(() => {
        setup();
        ready = true;
        if (typeof ResizeObserver !== 'undefined') (observer = new ResizeObserver(onResize)).observe(viewport);
        else window.addEventListener('resize', onResize, { passive: true });
        motion?.addEventListener('change', schedule);
      });
    });
  };
  const destroy = () => {
    destroyed = true;
    clearTimeout(timer);
    cancelAnimationFrame(raf);
    observer?.disconnect();
    window.removeEventListener('resize', onResize);
    motion?.removeEventListener('change', schedule);
    reset();
    store.map.delete(root);
    store.instances.delete(instance);
  };
  const instance: MarqueeInstance = { init, destroy };
  return instance;
};

export const initMarquees = () =>
  $$(`[${MARQUEE.root}]`).forEach((root) => {
    store.map.get(root)?.destroy();
    const instance = createMarquee(root);
    if (!instance) return;
    store.map.set(root, instance);
    store.instances.add(instance);
    instance.init();
  });
export const destroyMarquees = () => [...store.instances].forEach((instance) => instance.destroy());
